import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import { Tooltip } from "primereact/tooltip";
import { RefObject } from "react";
import { useTranslation } from "react-i18next";
import usePermissions from "../../../../../common/api/usePermissions";
import { PERMISSIONS } from "../../../../../common/constants";
import { ANNOUNCEMENT_STATUSES } from "../../../constants";
import { Announcement } from "../../../models";

interface ActionsBodyTemplateProps {
    rowData: Announcement;
    getMenuRef: (id: string) => RefObject<Menu | null>;
    onEdit: (announcement: Announcement) => void;
    onDelete: (announcement: Announcement) => void;
}

const ActionsBodyTemplate = (props: ActionsBodyTemplateProps) => {
    const { rowData, getMenuRef, onEdit, onDelete } = props;
    const { t } = useTranslation(["common", "announcements"]);
    const { hasPermissions } = usePermissions();

    const isArchived = rowData.status === ANNOUNCEMENT_STATUSES.ARCHIVED;
    const editItemId = `edit-item-${rowData.id}`;
    const menuId = `popup-menu-${rowData.id}`;
    const menuRef = getMenuRef(rowData.id);

    const canEdit = hasPermissions({
        globalPermissions: [PERMISSIONS.ANNOUNCEMENT.UPDATE],
    });
    const canDelete = hasPermissions({
        globalPermissions: [PERMISSIONS.ANNOUNCEMENT.DELETE],
    });

    const menuItems = [
        ...(canEdit
            ? [
                  {
                      label: t("common:edit"),
                      disabled: isArchived,
                      className: isArchived ? editItemId : undefined,
                      command: () => !isArchived && onEdit(rowData),
                  },
              ]
            : []),
        ...(canDelete
            ? [
                  {
                      label: t("common:delete"),
                      command: () => onDelete(rowData),
                  },
              ]
            : []),
    ];

    if (menuItems.length === 0) return null;

    return (
        <div className="flex items-center justify-end">
            <Menu model={menuItems} popup ref={menuRef} id={menuId} />
            {canEdit && isArchived && (
                <Tooltip
                    target={`.${editItemId}`}
                    content={t("announcements:table.archivedCannotEdit")}
                    position="left"
                />
            )}
            <Button
                icon="pi pi-ellipsis-v text-default"
                rounded
                text
                severity="secondary"
                aria-label={t("common:actions")}
                aria-haspopup
                onClick={(event) => {
                    menuRef.current?.toggle(event);
                }}
                aria-controls={menuId}
            />
        </div>
    );
};

export default ActionsBodyTemplate;
